import React from "react";
import { useRouter } from "next/router";

import { Org, OrgInfo, ProjectTypeName, User } from "src/lib/graphql/types";
import { TabOption, TabOptionKey } from "./ProfileData";
import { isUser, ReactSelectOption } from "src/lib/globalTypes";

import { contentTypeToPlural } from "src/components/index/BrowseAndExploreBanner";
import SortDropdown from "../SortDropdown";

type ProfileSortOption = {
  label: string;
  value: "createdAt" | "updatedAt" | "popularity";
};

type ContentFilterOption = keyof typeof ProjectTypeName | "All";

interface ProfileTabsProps {
  entity: User | Org;
  userIsMember: OrgInfo | undefined;
  tab: TabOption;
  setTab: React.Dispatch<React.SetStateAction<TabOption>>;
  sort: string;
  setSort: React.Dispatch<React.SetStateAction<string>>;
}

export default function ProfileTabs({
  entity,
  userIsMember,
  sort,
  setSort,
  tab,
  setTab,
}: ProfileTabsProps) {
  const router = useRouter();

  const { shortname } = router.query;

  const projectCount = entity.projectCount;

  const tabConstructor = () => {
    let tabOptions: TabOption[] = [
      { value: "All", alias: "All" },
      { value: "Simulation", alias: "Simulation" },
      { value: "Dataset", alias: "Dataset" },
      { value: "Behavior", alias: "Behavior" },
    ];

    if (isUser(entity)) {
      if (projectCount === 0) {
        tabOptions = [
          {
            value: "All",
            alias: "Project",
          },
        ];
      }
    } else {
      if (projectCount === 0) {
        tabOptions = [
          {
            value: "All",
            alias: "Project",
          },
        ];
      }

      if (userIsMember) {
        tabOptions.push({ value: "Members", alias: "Members" });
      }
    }

    return tabOptions;
  };

  const sortOptions: ProfileSortOption[] = [
    {
      value: "popularity",
      label: "Most popular",
    },
    {
      value: "updatedAt",
      label: "Last modified",
    },
    {
      value: "createdAt",
      label: "Last created",
    },
  ];

  const selectedSort = sortOptions.find((option) => option.value === sort);

  const tabUrlMap: Record<TabOptionKey, string> = {
    All: "projects",
    Behavior: "behaviors",
    Dataset: "data",
    Members: "members",
    Simulation: "simulations",
  };

  const tabs = tabConstructor();

  return tabs.length > 1 ? (
    <>
      <div className="title-and-new">
        <h2 className="large-text">Projects</h2>
      </div>
      <div className="type-and-sort">
        <div className="tab-header">
          {tabs.map((typeOption) => {
            const active = tab.value === typeOption.value;
            const onClick = () => {
              router.push(
                `/@${shortname}/${tabUrlMap[
                  typeOption.value
                ].toLocaleLowerCase()}`
              );
              setTab(typeOption);
            };
            return (
              <a onClick={onClick} key={typeOption.value}>
                <span className={"tab" + (active ? " active" : "")}>
                  {contentTypeToPlural[
                    typeOption.alias as ContentFilterOption
                  ] || "Starred"}
                </span>
              </a>
            );
          })}
        </div>
        <div className="sort">
          <SortDropdown
            value={selectedSort as ReactSelectOption}
            options={sortOptions as ReactSelectOption[]}
            onChange={(option) => setSort(option.value)}
          />
        </div>
      </div>
    </>
  ) : null;
}
